import { css } from '@emotion/react';
import { Chip, Link, Tooltip, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { FC, ReactNode, useMemo, useState } from 'react';
import { IFinanceRoundDataModel, isConvertibleRound } from '../../../data-models/finance-round.data-model';
import { roundsByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { colors } from '../../../theme/colors';
import { FMT } from '../../../util/formatter-service';
import {
  CurrencyCompact2dpFormatterDataModel,
  Pps2dpFormatterDataModel,
} from '../../../util/formatters/NumericFormatters';
import { ITransactionDataModel } from '../../../data-models/transaction.data-model';
import { CompanyTransactionsState } from '../../CompanyProfiles/state/CompanyFundingsState';
import { CollapsibleSectionCard } from './CollapsibleSectionCard';

const gridCss = css`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

const colCss = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-right: 12px;
  border-right: 1px solid ${colors.border.card};

  &:last-of-type {
    border-right: none;
    padding-right: 0;
  }
`;

const rowCss = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const labelCss = css`
  flex: 1;
  min-width: 0;
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const valueCss = css`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  line-height: 1.43;
  letter-spacing: 0.17px;
  color: ${colors.text.main};
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const coInvestorsSectionCss = css`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${colors.border.card};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const coInvestorsHeaderCss = css`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const coInvestorsCaptionCss = css`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${colors.text.secondary};
`;

const coInvestorsSummaryCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.secondary};
`;

const summaryCountCss = css`
  font-weight: 600;
  color: ${colors.text.main};
`;

const tagListCss = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

// Outlined pills per Sam's UX sketch — never a filled chip. Display-only:
// pointer-events off so nothing about them reads as clickable.
const investorChipSx = {
  backgroundColor: 'transparent',
  border: `1px solid ${colors.neutral[30]}`,
  color: colors.text.main,
  py: 4,
  px: 5,
  pointerEvents: 'none',
} as const;

/** Pills shown while collapsed: leads plus the next-largest co-investors (array order = BE ranking). */
export const INVESTOR_COLLAPSE_LIMIT = 5;

export interface RoundField {
  label: string;
  value: ReactNode;
  /** Hover-tooltip detail (the exact value, a clause, …). Omitted renders the value with no tooltip. */
  exact?: string;
}

export const Row: FC<RoundField> = ({ label, value, exact }) => (
  <div css={rowCss}>
    <span css={labelCss}>{label}</span>
    {/* div (not span): values may be block/flex elements, e.g. LeadInvestorsDisplay */}
    {exact ? (
      <div css={valueCss}>
        {/* Anchor the tooltip to the text, not the flex-stretched cell: the value is
            right-aligned, so a cell-anchored tooltip floats detached from the number.
            No arrow: the theme styles only the tooltip slot, so an arrow renders
            MUI's default dark grey against the house white bubble. */}
        <Tooltip title={exact} placement='top'>
          <span>{value}</span>
        </Tooltip>
      </div>
    ) : (
      <div css={valueCss}>{value}</div>
    )}
  </div>
);

/** Highest price among the round's securities (the Security Overview), else among the firm's transactions on it. */
export function selectHighestPps(
  transactions: ITransactionDataModel[],
  round: Pick<IFinanceRoundDataModel, 'id' | 'dealSecurities'>
): number | null {
  // reduce (not Math.max(...spread)): a large transaction array spread as call
  // arguments can throw a RangeError, which would blank the whole Round Overview.
  const fromSecurities = (round.dealSecurities ?? []).reduce<number | null>((max, s) => {
    if (s.pricePerShare == null) {
      return max;
    }
    return max == null || s.pricePerShare > max ? s.pricePerShare : max;
  }, null);
  if (fromSecurities != null) {
    return fromSecurities;
  }
  return transactions.reduce<number | null>((max, t) => {
    if (t.investmentRoundId !== round.id || t.pricePerShare == null) {
      return max;
    }
    return max == null || t.pricePerShare > max ? t.pricePerShare : max;
  }, null);
}

/**
 * Whether every Round Overview value is missing. Round Name is excluded: it is
 * always present and duplicates the round list. Numerics use `== null` — 0 is a
 * value; roundDate is a string defaulting to '' so it takes a falsy check.
 * Raw-field checks match the rendered blanks because FMT formatters blank nulls
 * and the co-investors section hides itself without investor data.
 */
export function isRoundOverviewEmpty(
  round: IFinanceRoundDataModel,
  investmentRoundName: string | null | undefined,
  highestPps: number | null
): boolean {
  return (
    !investmentRoundName &&
    !round.roundDate &&
    round.raiseAmount == null &&
    round.preMoneyValuation == null &&
    round.postMoneyValuation == null &&
    round.roundDilution == null &&
    round.ownershipPercentage == null &&
    round.totalHolders == null &&
    (round.leadInvestors ?? []).length === 0 &&
    (round.coInvestors ?? []).length === 0 &&
    highestPps == null &&
    round.newSharesIssued == null &&
    round.fullyDilutedShares == null
  );
}

interface RoundOverviewCardProps {
  round: IFinanceRoundDataModel;
  companyId: number;
}

/**
 * Round Overview section of the Deals-tab detail pane (Figma 2022:4533): a
 * four-column key/value grid of round-level metrics for the selected round.
 * Values run through the FMT registry; missing values fall back to each
 * formatter's default (blank).
 */
export const RoundOverviewCard: FC<RoundOverviewCardProps> = ({ round, companyId }) => {
  const roundsByIdMap = useAtomValue(roundsByIdMapAtom);
  const transactions = useAtomValue(CompanyTransactionsState(companyId));
  const investmentRound = round.roundId ? roundsByIdMap.get(round.roundId) : null;

  // Highest price per share across the round's securities: lets users compare PPS
  // between security classes issued in the round (e.g. Series C vs Series C-1).
  const highestPps = useMemo(() => selectHighestPps(transactions, round), [transactions, round]);

  return (
    <RoundOverviewCardView round={round} stageLabel={investmentRound?.displayName} highestPps={highestPps} />
  );
};

export interface RoundOverviewCardViewProps {
  round: IFinanceRoundDataModel;
  stageLabel: string | null | undefined;
  highestPps: number | null;
}

/** Rendering half of the card; the container resolves stage and highest PPS from the store, a preview supplies its own. */
export const RoundOverviewCardView: FC<RoundOverviewCardViewProps> = ({ round, stageLabel, highestPps }) => {
  const columns: RoundField[][] = [
    [
      { label: 'Round Name', value: round.name || 'Unknown Round' },
      { label: 'Round Stage', value: stageLabel ?? '' },
      { label: 'Round Closing Date', value: FMT.format('date', round.roundDate) },
    ],
    [
      // One dp setting across all three: mixed precision in a column reads as a glitch.
      {
        label: 'Total New Money',
        value: FMT.format(CurrencyCompact2dpFormatterDataModel, round.raiseAmount),
        exact: FMT.format('currency', round.raiseAmount),
      },
      {
        label: 'Pre-Money Valuation',
        value: FMT.format(CurrencyCompact2dpFormatterDataModel, round.preMoneyValuation),
        exact: FMT.format('currency', round.preMoneyValuation),
      },
      {
        label: 'Post-Money Valuation',
        value: FMT.format(CurrencyCompact2dpFormatterDataModel, round.postMoneyValuation),
        exact: FMT.format('currency', round.postMoneyValuation),
      },
      { label: 'Round Dilution', value: FMT.format('percent', round.roundDilution) },
    ],
    [
      { label: 'New Shares Issued', value: FMT.format('integer', round.newSharesIssued) },
      { label: 'FDS at Close', value: FMT.format('integer', round.fullyDilutedShares) },
      { label: 'Highest Price Per Share', value: FMT.format(Pps2dpFormatterDataModel, highestPps) },
      { label: 'Ownership at Close', value: FMT.format('percent', round.ownershipPercentage) },
    ],
  ];

  // Leads first (deduped from the co-investor list so a name never shows twice), then
  // co-investors in array order — the round tracker writes Aumni co-investors
  // largest-invested first, so the collapsed five are the lead(s) plus the
  // next-largest names.
  const [showAllInvestors, setShowAllInvestors] = useState(false);
  const orderedInvestors = useMemo<string[]>(() => {
    const leads = round.leadInvestors ?? [];
    return [...leads, ...(round.coInvestors ?? []).filter((name) => !leads.includes(name))];
  }, [round.leadInvestors, round.coInvestors]);
  const namedCount = orderedInvestors.length;
  const visibleInvestors = showAllInvestors
    ? orderedInvestors
    : orderedInvestors.slice(0, INVESTOR_COLLAPSE_LIMIT);

  const hasInvestorData = namedCount > 0 || round.totalHolders != null;

  return (
    <CollapsibleSectionCard
      title='Round Overview'
      isEmpty={isRoundOverviewEmpty(round, stageLabel, highestPps)}
      defaultCollapsed={isConvertibleRound(round)}
    >
      <div css={gridCss}>
        {columns.map((fields) => (
          <div css={colCss} key={fields.map((f) => f.label).join('|')}>
            {fields.map((f) => (
              <Row key={f.label} label={f.label} value={f.value} exact={f.exact} />
            ))}
          </div>
        ))}
      </div>
      {hasInvestorData && (
        <div css={coInvestorsSectionCss}>
          <div css={coInvestorsHeaderCss}>
            <span css={coInvestorsCaptionCss}>Co-Investors</span>
            {round.totalHolders != null && (
              <span css={coInvestorsSummaryCss}>
                <span css={summaryCountCss}>{FMT.format('integer', round.totalHolders)}</span> investors
              </span>
            )}
          </div>
          {namedCount > 0 && (
            <div css={tagListCss} aria-label='Co-investors'>
              {visibleInvestors.map((name) => (
                <Chip
                  key={name}
                  label={<Typography variant='body2'>{name}</Typography>}
                  sx={investorChipSx}
                />
              ))}
              {namedCount > INVESTOR_COLLAPSE_LIMIT && (
                <Link
                  color={'secondary'}
                  component='button'
                  underline='hover'
                  onClick={() => setShowAllInvestors((prev) => !prev)}
                >
                  {showAllInvestors ? 'Show Less' : `Show All (${namedCount})`}
                </Link>
              )}
            </div>
          )}
        </div>
      )}
    </CollapsibleSectionCard>
  );
};
